import { z } from "zod";
import { defineStepConfig } from "../../../registry/helpers";

/**
 * Schema for individual Oracle order number result.
 */
const erpOrderResultSchema = z.object({
  /** Null for registered-API submissions (no SalesOrder row exists). */
  salesOrderId: z.string().uuid().nullable(),
  /** The draft polled via its submission record (registered-API path). */
  draftSalesOrderId: z.string().uuid().nullable().optional(),
  stagedPurchaseOrderId: z.string().uuid().nullable(),
  oracleOrderNumber: z.string().nullable(),
  origSysDocRef: z.string().nullable(),
  found: z.boolean(),
});

/**
 * Await ERP Order Numbers Step
 *
 * Polls the ERP (Oracle EBS) to retrieve assigned order numbers for submitted
 * sales orders. Uses exponential backoff to wait for Oracle to process the
 * orders and assign order numbers.
 *
 * This step is designed to run after the submitSalesOrders step to allow time
 * for Oracle's asynchronous order import process to complete.
 *
 * The step will:
 * 1. Load sales orders to check current originMetadata and origSysDocRef
 * 2. Poll the ERP via fetchOrderStatuses() with exponential backoff
 * 3. Return results with whatever data is available when polling completes
 *
 * Polling exits when:
 * - All orders have ERP order numbers assigned
 * - Maximum wait time is exceeded
 */
export const awaitErpOrderNumbersStep = defineStepConfig({
  type: "await_erp_order_numbers",
  name: "Await ERP Order Numbers",
  description:
    "Polls the ERP (Oracle EBS) to retrieve assigned order numbers for submitted sales orders",
  category: "Data Access",
  libraryModule: "OrderManagement",
  inputSchema: z.object({
    salesOrderIds: z
      .array(z.string().uuid())
      .describe("UUIDs of the sales orders to poll for Oracle order numbers"),
    draftSalesOrderIds: z
      .array(z.string().uuid())
      .optional()
      .describe(
        "UUIDs of drafts submitted via a REGISTERED submission API — polled through their submission record's correlation ref (no SalesOrder row exists)",
      ),
  }),

  outputSchema: z.object({
    skipped: z
      .boolean()
      .default(false)
      .describe("Whether the step was skipped (e.g., no sales orders to poll)"),
    skippedReason: z
      .string()
      .optional()
      .describe("Reason for skipping, if skipped is true"),
    results: z
      .array(erpOrderResultSchema)
      .describe("Results for each sales order polled"),
    orderNumbers: z
      .array(z.string())
      .default([])
      .describe(
        "Oracle order numbers found this run (found results only) — empty when the step skipped or nothing landed before timeout",
      ),
    allFound: z.boolean().describe("Whether all orders have Oracle numbers"),
    foundCount: z.number().describe("Number of orders with Oracle numbers"),
    notFoundCount: z
      .number()
      .describe("Number of orders still waiting for Oracle numbers"),
  }),

  configSchema: z.object({
    maxWaitTimeMs: z
      .number()
      .default(300000)
      .describe(
        "Maximum time to wait for Oracle order numbers (default: 5 minutes)",
      ),
    initialDelayMs: z
      .number()
      .default(10000)
      .describe("Initial delay between polls (default: 10 seconds)"),
    maxDelayMs: z
      .number()
      .default(60000)
      .describe("Maximum delay between polls (default: 60 seconds)"),
    backoffMultiplier: z
      .number()
      .default(2)
      .describe("Multiplier for exponential backoff (default: 2)"),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "10 minutes",
    retry: {
      maximumAttempts: 2,
    },
  },
});

export type ErpOrderResult = z.infer<typeof erpOrderResultSchema>;
